
import * as React from "react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface Airport {
  id: number;
  name: string;
  iata_code: string | null;
  location: string;
}

interface AirportSearchProps {
  onSelect: (airport: Airport) => void;
  selectedAirport?: {
    name: string;
    location: string;
  };
}

export function AirportSearch({
  onSelect,
  selectedAirport
}: AirportSearchProps) {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState(selectedAirport?.name || "");
  const [search, setSearch] = React.useState("");
  const [airports, setAirports] = React.useState<Airport[]>([]);

  React.useEffect(() => {
    if (selectedAirport?.name && !value) {
      setValue(selectedAirport.name);
    }
  }, [selectedAirport, value]);

  React.useEffect(() => {
    const fetchAirports = async () => {
      if (search.length === 0) {
        const { data, error } = await supabase
          .from('airports')
          .select('id, name, iata_code, location')
          .limit(10);
        if (!error && data) {
          setAirports(data);
        }
      } else {
        const searchQuery = search.split(' ').filter(Boolean).map(word => `${word}:*`).join(' & ');
        const { data, error } = await supabase
          .from('airports')
          .select('id, name, iata_code, location')
          .textSearch('fts', searchQuery)
          .limit(10);
        if (!error && data) {
          setAirports(data);
        }
      }
    };
    fetchAirports();
  }, [search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          role="combobox" 
          aria-expanded={open} 
          className="w-full justify-between my-[8px]"
        >
          {value || "Search for an airport..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Search airports..." 
            value={search} 
            onValueChange={setSearch} 
          />
          <CommandList>
            <CommandEmpty>No airport found.</CommandEmpty>
            <CommandGroup>
              {airports.map(airport => (
                <CommandItem 
                  key={airport.id} 
                  value={airport.name}
                  onSelect={currentValue => {
                    setValue(currentValue);
                    onSelect(airport);
                    setOpen(false);
                  }}
                >
                  <Check className={cn(
                    "mr-2 h-4 w-4",
                    value === airport.name ? "opacity-100" : "opacity-0"
                  )} />
                  <div>
                    <div className="flex items-center">
                      <span>{airport.name}</span>
                      {airport.iata_code && (
                        <span className="ml-2 rounded-md bg-muted px-2 text-sm">
                          {airport.iata_code}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {airport.location}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
